import type { components } from "../../../api/generated";
import type {
  AuthzChangeItem,
  AuthzRoleConfiguration,
  AuthzRoleConfigurationMutation,
  AuthzRoleCreateMutation,
  AuthzRoleItem,
  AuthzRoleListResult,
  AuthzTreeNode,
} from "../types";

export type AuthzRoleListSuccessResponse = components["schemas"]["RoleListSuccessResponse"];
export type AuthzRoleSuccessResponse = components["schemas"]["RoleSuccessResponse"];
export type AuthzRoleConfigurationSuccessResponse = components["schemas"]["RoleConfigurationSuccessResponse"];
export type AuthzRoleMutationRequest = components["schemas"]["RoleMutation"];
export type AuthzRoleStatusMutationRequest = components["schemas"]["RoleStatusMutation"];
export type AuthzRoleConfigurationMutationRequest = components["schemas"]["RoleConfigurationMutation"];

type RawRoleItem = components["schemas"]["RoleItem"];
type RawTreeNode = components["schemas"]["RoleTreeNode"];
type RawChangeItem = components["schemas"]["AuthzChangeItem"];
type RawConfiguration = components["schemas"]["RoleConfigurationPayload"];

/** 映射角色列表项。 */
export function mapAuthzRoleItem(raw: RawRoleItem): AuthzRoleItem {
  return {
    id: raw.id,
    code: raw.code,
    name: raw.name,
    description: raw.description,
    status: raw.status === "disabled" ? "disabled" : "enabled",
    isSystem: raw.is_system,
    version: raw.version,
    memberCount: raw.member_count,
    pagePermissionCount: raw.page_permission_count,
    actionPermissionCount: raw.action_permission_count,
    updatedAt: raw.updated_at,
  };
}

/** 映射角色分页结果。 */
export function mapAuthzRoleList(raw: components["schemas"]["RoleListPayload"]): AuthzRoleListResult {
  return {
    items: (raw.items ?? []).map(mapAuthzRoleItem),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
  };
}

/** 映射树节点。 */
export function mapAuthzTreeNode(raw: RawTreeNode): AuthzTreeNode {
  return {
    key: raw.key,
    type: raw.type,
    title: raw.title,
    children: (raw.children ?? []).map(mapAuthzTreeNode),
    permissionCode: raw.permission_code ?? undefined,
    riskLevel: raw.risk_level,
    disabled: raw.disabled,
    userId: raw.user_id ?? undefined,
    deptId: raw.dept_id ?? undefined,
    otherDeptIds: raw.other_dept_ids ?? [],
  };
}

/** 映射最近授权变化。 */
export function mapAuthzChangeItem(raw: RawChangeItem): AuthzChangeItem {
  return {
    id: raw.id,
    operatorUserId: raw.operator_user_id,
    operatorName: raw.operator_name,
    summary: raw.summary,
    addedUserIds: raw.added_user_ids ?? [],
    removedUserIds: raw.removed_user_ids ?? [],
    addedPermissionCodes: raw.added_permission_codes ?? [],
    removedPermissionCodes: raw.removed_permission_codes ?? [],
    createdAt: raw.created_at,
  };
}

/** 映射角色详情配置。 */
export function mapAuthzRoleConfiguration(raw: RawConfiguration): AuthzRoleConfiguration {
  return {
    role: mapAuthzRoleItem(raw.role),
    memberTree: (raw.member_tree ?? []).map(mapAuthzTreeNode),
    permissionTree: (raw.permission_tree ?? []).map(mapAuthzTreeNode),
    selectedUserIds: raw.selected_user_ids ?? [],
    selectedPermissionCodes: raw.selected_permission_codes ?? [],
    invalidMembers: (raw.invalid_members ?? []).map((item) => ({
      userId: item.user_id,
      name: item.name,
      reason: item.reason,
    })),
    recentChanges: (raw.recent_changes ?? []).map(mapAuthzChangeItem),
  };
}

/** 映射新增角色请求。 */
export function mapAuthzRoleCreateMutation(payload: AuthzRoleCreateMutation): AuthzRoleMutationRequest {
  return {
    code: payload.code.trim(),
    name: payload.name.trim(),
    description: payload.description.trim(),
  };
}

/** 映射统一保存请求。 */
export function mapAuthzRoleConfigurationMutation(
  payload: AuthzRoleConfigurationMutation,
): AuthzRoleConfigurationMutationRequest {
  return {
    version: payload.version,
    name: payload.name.trim(),
    description: payload.description.trim(),
    user_ids: [...new Set(payload.userIds)],
    permission_codes: [...new Set(payload.permissionCodes)],
  };
}
